import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { Card, SectionTitle } from '@/components/Card';
import { Badge, type BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/LoadingScreen';
import { useBusinessUnits } from '@/app/hooks';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import { useOperationalAccess } from '@/app/useOperationalAccess';
import { ApiRequestError } from '@/services/api';
import { documentsService, suppliersService } from '@/services/operational.service';
import {
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_TITLES,
  PROCESSING_STATUS_TITLES,
  type DocumentType,
  type ProcessingStatus,
} from '@/types/operational';
import { formatDate } from '@/features/operational/money';
import { v, font } from '@/theme/tokens';

const STATUS_TONE: Record<ProcessingStatus, BadgeTone> = {
  NOT_CONFIGURED: 'neutral',
  PENDING: 'neutral',
  RUNNING: 'accent',
  COMPLETED: 'positive',
  FAILED: 'negative',
};

const inputStyle = {
  padding: '7px 10px',
  border: `1px solid ${v('line')}`,
  borderRadius: 9,
  background: v('surface'),
  color: v('ink'),
  fontSize: 12.5,
  maxWidth: '100%',
  minWidth: 0,
} as const;

/**
 * Документы: оригинал → армянский текст → русский перевод.
 *
 * The three are shown as three panes and never as one. The Armenian is what
 * the provider read and is the thing to check against the scan; the Russian
 * is a convenience for the people reading it. A structured reading is always
 * labelled as a proposal, and confirming one produces a **draft** invoice
 * that still has to be accepted in «Поставщики и долги».
 */
export function DocumentsPage() {
  const access = useOperationalAccess();
  const wide = useIsWideScreen();
  const client = useQueryClient();
  const units = useBusinessUnits();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const providers = useQuery({
    queryKey: ['operational', 'documents', 'providers'],
    queryFn: ({ signal }) => documentsService.providers(signal),
    enabled: access.can('DOCUMENTS_OCR'),
    staleTime: 10 * 60_000,
  });

  const documents = useQuery({
    queryKey: ['operational', 'documents', 'list'],
    queryFn: ({ signal }) => documentsService.list({}, signal),
    enabled: access.can('DOCUMENTS_OCR'),
  });

  const detail = useQuery({
    queryKey: ['operational', 'documents', 'detail', selectedId],
    queryFn: ({ signal }) => documentsService.detail(selectedId as string, signal),
    enabled: Boolean(selectedId),
  });

  const suppliers = useQuery({
    queryKey: ['operational', 'suppliers', ''],
    queryFn: ({ signal }) => suppliersService.list('', signal),
    enabled: access.can('DOCUMENTS_OCR'),
  });

  const refresh = () => void client.invalidateQueries({ queryKey: ['operational'] });
  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiRequestError ? e.message : fallback);

  const upload = useMutation({
    mutationFn: documentsService.upload,
    onSuccess: (data) => {
      setSelectedId(data.id);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось загрузить документ'),
  });

  const runOcr = useMutation({
    mutationFn: (id: string) => documentsService.ocr(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail('Не удалось распознать документ'),
  });

  const runTranslate = useMutation({
    mutationFn: (id: string) => documentsService.translate(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail('Не удалось перевести текст'),
  });

  const runExtract = useMutation({
    mutationFn: (id: string) => documentsService.extract(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail('Не удалось разобрать документ'),
  });

  if (access.loading) return <Spinner />;
  if (!access.can('DOCUMENTS_OCR')) {
    return (
      <>
        <PageHeader title="Документы" />
        <EmptyState
          icon="lock"
          title="Модуль недоступен"
          description="Доступ к документам и распознаванию выдаёт владелец."
        />
      </>
    );
  }

  const canUpload = access.can('DOCUMENTS_OCR', 'UPLOAD');
  const canManage = access.can('DOCUMENTS_OCR', 'MANAGE');
  const doc = detail.data;

  return (
    <>
      <PageHeader
        title="Документы"
        subtitle="Оригинал, армянский текст и русский перевод хранятся отдельно"
      />

      {error && (
        <div
          role="alert"
          style={{
            background: v('negative-soft'),
            color: v('negative'),
            padding: '9px 12px',
            borderRadius: 10,
            fontSize: 13,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {providers.data && !providers.data.ocr_configured && (
        <Card style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: v('ink-sub') }}>
            Распознавание текста не настроено на сервере. Документы можно загружать и хранить;
            армянский текст и перевод появятся после подключения провайдера OCR.
          </div>
        </Card>
      )}

      {canUpload && (
        <UploadCard
          units={units.data ?? []}
          suppliers={suppliers.data ?? []}
          busy={upload.isPending}
          onUpload={(input) => upload.mutate(input)}
        />
      )}

      <Card style={{ marginBottom: 18 }}>
        <SectionTitle right={<Badge tone="neutral">{documents.data?.length ?? 0}</Badge>}>
          Загруженные документы
        </SectionTitle>

        {documents.isPending && <Spinner />}
        {documents.data?.length === 0 && (
          <div style={{ fontSize: 13, color: v('ink-muted') }}>Документов пока нет.</div>
        )}

        {(documents.data ?? []).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSelectedId(item.id)}
            style={{
              display: 'flex',
              width: '100%',
              textAlign: 'left',
              gap: 12,
              padding: '11px 0',
              borderTop: `1px solid ${v('line-soft')}`,
              background: 'transparent',
              border: 'none',
              borderTopStyle: 'solid',
              cursor: 'pointer',
              alignItems: wide ? 'center' : 'flex-start',
              flexDirection: wide ? 'row' : 'column',
              color: v('ink'),
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5, overflowWrap: 'anywhere' }}>
                {item.original_filename}
              </div>
              <div style={{ fontSize: 12, color: v('ink-muted') }}>
                {item.document_type_title} · {item.business_unit_name}
                {item.supplier_name ? ` · ${item.supplier_name}` : ''} ·{' '}
                {formatDate(item.uploaded_at)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {item.low_confidence && <Badge tone="warning">Низкая уверенность</Badge>}
              <Badge tone={STATUS_TONE[item.ocr_status]}>
                OCR: {PROCESSING_STATUS_TITLES[item.ocr_status]}
              </Badge>
              <Badge tone={STATUS_TONE[item.translation_status]}>
                Перевод: {PROCESSING_STATUS_TITLES[item.translation_status]}
              </Badge>
            </div>
          </button>
        ))}
      </Card>

      {selectedId && detail.isPending && <Spinner />}

      {doc && (
        <>
          <Card style={{ marginBottom: 18 }}>
            <SectionTitle
              right={
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {canUpload && providers.data?.ocr_configured && (
                    <Button
                      style={{ padding: '6px 12px', fontSize: 12.5 }}
                      disabled={runOcr.isPending}
                      onClick={() => runOcr.mutate(doc.id)}
                    >
                      {runOcr.isPending ? 'Распознавание…' : 'Распознать'}
                    </Button>
                  )}
                  {canUpload && doc.has_ocr_text && providers.data?.translation_configured && (
                    <Button
                      style={{ padding: '6px 12px', fontSize: 12.5 }}
                      disabled={runTranslate.isPending}
                      onClick={() => runTranslate.mutate(doc.id)}
                    >
                      {runTranslate.isPending ? 'Перевод…' : 'Перевести'}
                    </Button>
                  )}
                  {canUpload && doc.has_ocr_text && (
                    <Button
                      style={{ padding: '6px 12px', fontSize: 12.5 }}
                      disabled={runExtract.isPending}
                      onClick={() => runExtract.mutate(doc.id)}
                    >
                      Разобрать
                    </Button>
                  )}
                  <Button
                    style={{ padding: '6px 12px', fontSize: 12.5 }}
                    onClick={() => setSelectedId(null)}
                  >
                    Закрыть
                  </Button>
                </div>
              }
            >
              {doc.original_filename}
            </SectionTitle>

            {doc.low_confidence && (
              <div
                role="alert"
                style={{
                  background: v('warning-soft'),
                  color: v('warning'),
                  padding: '8px 11px',
                  borderRadius: 9,
                  fontSize: 12.5,
                  marginBottom: 12,
                }}
              >
                Распознавание с низкой уверенностью
                {doc.ocr_confidence != null
                  ? ` (${Math.round(doc.ocr_confidence * 100)}%)`
                  : ''}
                . Сверьте текст с оригиналом.
              </div>
            )}
            {doc.ocr_error && (
              <div style={{ fontSize: 12.5, color: v('negative'), marginBottom: 10 }}>
                OCR: {doc.ocr_error}
              </div>
            )}
            {doc.translation_error && (
              <div style={{ fontSize: 12.5, color: v('negative'), marginBottom: 10 }}>
                Перевод: {doc.translation_error}
              </div>
            )}

            <div
              style={{
                display: 'grid',
                // Three panes side by side where there is room, stacked on a
                // phone. The Armenian and the Russian are never merged into
                // one pane — the original text has to stay checkable.
                gridTemplateColumns: wide ? 'repeat(3, minmax(0, 1fr))' : '1fr',
                gap: 14,
              }}
            >
              <Pane title="Оригинал">
                <a
                  href={documentsService.fileUrl(doc.id)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 13 }}
                >
                  Открыть {doc.mime_type.includes('pdf') ? 'PDF' : 'изображение'} →
                </a>
                <div style={{ fontSize: 11.5, color: v('ink-muted'), marginTop: 8 }}>
                  {Math.round(doc.size_bytes / 1024)} КБ · {doc.mime_type}
                </div>
              </Pane>

              <Pane title="Армянский текст">
                {doc.ocr_text_hy ? (
                  <pre style={preStyle}>{doc.ocr_text_hy}</pre>
                ) : (
                  <span style={{ fontSize: 12.5, color: v('ink-muted') }}>
                    Текст ещё не распознан
                  </span>
                )}
              </Pane>

              <Pane title="Русский перевод">
                {doc.translated_text_ru ? (
                  <pre style={preStyle}>{doc.translated_text_ru}</pre>
                ) : (
                  <span style={{ fontSize: 12.5, color: v('ink-muted') }}>Перевода ещё нет</span>
                )}
              </Pane>
            </div>
          </Card>

          {doc.extractions.length > 0 && (
            <Card>
              <SectionTitle>Разбор документа</SectionTitle>
              <p style={{ fontSize: 12, color: v('ink-faint'), marginTop: 0 }}>
                Это предложение системы, а не подтверждённые данные. После проверки человеком из
                него создаётся <strong>черновик</strong> счёта, который подтверждается отдельно в
                разделе «Поставщики и долги».
              </p>
              {doc.extractions.map((extraction) => (
                <ExtractionRow
                  key={extraction.id}
                  extraction={extraction}
                  suppliers={suppliers.data ?? []}
                  canManage={canManage}
                  onConfirmed={refresh}
                  onError={setError}
                />
              ))}
            </Card>
          )}
        </>
      )}
    </>
  );
}

const preStyle = {
  margin: 0,
  fontSize: 12.5,
  fontFamily: font.mono,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  maxHeight: 320,
  overflowY: 'auto',
  lineHeight: 1.5,
} as const;

function Pane({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: v('ink-muted'),
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: v('panel'),
          border: `1px solid ${v('line-soft')}`,
          borderRadius: 10,
          padding: 12,
          minHeight: 90,
          minWidth: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * One proposed reading.
 *
 * The numeric check is shown before anything else: a figure that cannot be
 * traced back to a number the OCR read is exactly the thing a person must
 * look at, and the system will not quietly correct it.
 */
function ExtractionRow({
  extraction,
  suppliers,
  canManage,
  onConfirmed,
  onError,
}: {
  extraction: {
    id: string;
    status: string;
    payload: Record<string, unknown>;
    numeric_check: Record<string, unknown> | null;
    created_invoice_id?: string | null;
  };
  suppliers: { id: string; name: string }[];
  canManage: boolean;
  onConfirmed: () => void;
  onError: (message: string) => void;
}) {
  const payload = extraction.payload as {
    document_number?: string | null;
    document_date?: string | null;
    document_total?: string | null;
  };
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '');
  const [amount, setAmount] = useState(payload.document_total ?? '');
  const [documentDate, setDocumentDate] = useState(
    payload.document_date ?? new Date().toISOString().slice(0, 10),
  );
  const [number, setNumber] = useState(payload.document_number ?? '');

  const confirm = useMutation({
    mutationFn: () =>
      documentsService.confirmExtraction(extraction.id, {
        supplier_id: supplierId,
        document_date: documentDate,
        amount: String(amount).replace(',', '.'),
        currency: 'AMD',
        document_number: number || undefined,
      }),
    onSuccess: onConfirmed,
    onError: (e: unknown) =>
      onError(e instanceof ApiRequestError ? e.message : 'Не удалось подтвердить разбор'),
  });

  const reject = useMutation({
    mutationFn: () => documentsService.rejectExtraction(extraction.id),
    onSuccess: onConfirmed,
    onError: (e: unknown) =>
      onError(e instanceof ApiRequestError ? e.message : 'Не удалось отклонить разбор'),
  });

  const check = extraction.numeric_check as
    | { ok?: boolean; checked?: number; traceable?: number; untraceable?: { field: string; value: string }[] }
    | null;

  return (
    <div style={{ borderTop: `1px solid ${v('line-soft')}`, paddingTop: 12, marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <Badge
          tone={
            extraction.status === 'CONFIRMED'
              ? 'positive'
              : extraction.status === 'REJECTED'
                ? 'neutral'
                : 'warning'
          }
        >
          {extraction.status === 'CONFIRMED'
            ? 'Подтверждено'
            : extraction.status === 'REJECTED'
              ? 'Отклонено'
              : 'Предложение'}
        </Badge>
        {check && (
          <Badge tone={check.ok ? 'positive' : 'negative'}>
            {check.ok
              ? `Все числа найдены в тексте (${check.traceable}/${check.checked})`
              : `Не найдено в тексте: ${check.untraceable?.length ?? 0}`}
          </Badge>
        )}
      </div>

      {check && !check.ok && (check.untraceable?.length ?? 0) > 0 && (
        <div
          role="alert"
          style={{
            background: v('negative-soft'),
            color: v('negative'),
            padding: '8px 11px',
            borderRadius: 9,
            fontSize: 12.5,
            marginBottom: 10,
          }}
        >
          Эти значения не найдены в распознанном тексте и могли быть искажены:{' '}
          {(check.untraceable ?? []).map((item) => `${item.field} = ${item.value}`).join(', ')}.
          Проверьте их по оригиналу — система их не исправляет.
        </div>
      )}

      {extraction.status === 'PROPOSED' && canManage ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Поставщик</span>
            <select
              aria-label="Поставщик счёта"
              value={supplierId}
              onChange={(event) => setSupplierId(event.target.value)}
              style={inputStyle}
            >
              <option value="">Выберите…</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Номер</span>
            <input
              aria-label="Номер документа из разбора"
              value={number}
              onChange={(event) => setNumber(event.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Дата</span>
            <input
              type="date"
              aria-label="Дата документа из разбора"
              value={documentDate}
              onChange={(event) => setDocumentDate(event.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Сумма</span>
            <input
              aria-label="Сумма из разбора"
              inputMode="decimal"
              value={amount ?? ''}
              onChange={(event) => setAmount(event.target.value)}
              style={inputStyle}
            />
          </label>
          <Button
            variant="primary"
            disabled={confirm.isPending || !supplierId || !String(amount).trim()}
            style={{ padding: '7px 12px', fontSize: 12.5 }}
            onClick={() => confirm.mutate()}
          >
            Создать черновик счёта
          </Button>
          <Button
            style={{ padding: '7px 12px', fontSize: 12.5 }}
            disabled={reject.isPending}
            onClick={() => reject.mutate()}
          >
            Отклонить
          </Button>
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: v('ink-muted') }}>
          {payload.document_number ? `№ ${payload.document_number} · ` : ''}
          {payload.document_date ? `${formatDate(payload.document_date)} · ` : ''}
          {payload.document_total ?? '—'}
          {extraction.created_invoice_id ? ' · создан черновик счёта' : ''}
        </div>
      )}
    </div>
  );
}

function UploadCard({
  units,
  suppliers,
  busy,
  onUpload,
}: {
  units: { id: string; name: string }[];
  suppliers: { id: string; name: string }[];
  busy: boolean;
  onUpload: (input: {
    file: File;
    businessUnitId: string;
    documentType: string;
    supplierId?: string;
  }) => void;
}) {
  const [unitId, setUnitId] = useState('');
  const [type, setType] = useState<DocumentType>('SUPPLIER_INVOICE');
  const [supplierId, setSupplierId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  return (
    <Card style={{ marginBottom: 18 }}>
      <SectionTitle>Загрузить документ</SectionTitle>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Объект</span>
          <select
            aria-label="Объект документа"
            value={unitId}
            onChange={(event) => setUnitId(event.target.value)}
            style={inputStyle}
          >
            <option value="">Выберите…</option>
            {units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Тип</span>
          <select
            aria-label="Тип документа"
            value={type}
            onChange={(event) => setType(event.target.value as DocumentType)}
            style={inputStyle}
          >
            {DOCUMENT_TYPES.map((code) => (
              <option key={code} value={code}>
                {DOCUMENT_TYPE_TITLES[code]}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Поставщик</span>
          <select
            aria-label="Поставщик документа"
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
            style={inputStyle}
          >
            <option value="">Не указан</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Файл (PDF / JPG / PNG)</span>
          <input
            type="file"
            aria-label="Файл документа"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            style={{ ...inputStyle, padding: '6px 8px' }}
          />
        </label>

        <Button
          variant="primary"
          disabled={busy || !unitId || !file}
          onClick={() => {
            if (!file) return;
            onUpload({
              file,
              businessUnitId: unitId,
              documentType: type,
              supplierId: supplierId || undefined,
            });
          }}
        >
          {busy ? 'Загрузка…' : 'Загрузить'}
        </Button>
      </div>
    </Card>
  );
}
